// ══════════════════════════════════════════════════════════
//  #FLEET_OEM_HOURS_V1 (2026-10-08) — factory (OEM) rig-hour feeds: Ditch Witch · Vermeer · Kobelco
//
//  Client side of api/oem-hours-sync.js. That function writes ONE record, `fleet-oem-hours`:
//    { v, generatedAt, week, staleDays,
//      providers: { ditchwitch|vermeer|kobelco: { label, status:'ok'|'waiting'|'error', at, checkedAt,
//                                                 machines, withHours, stale, message } },
//      devices:   [ { provider, vin, ids:[…], model, engineHours, engineHoursAt, idleHours, lat, lon,
//                     fuelPct, defPct, running, stale, weekly:{ <mondayISO>:{ h, at } } } ] }
//
//  Everything here is a pure helper so FleetModule (Weekly Inventory + Bob's dash) and FleetMaint read
//  the feed the same way. Standing rule, same as Geotab: join on Serial / VIN, never on a typed name.
// ══════════════════════════════════════════════════════════
import { vinClean } from "./unitValid";

export const OEM_KEY = 'fleet-oem-hours';

// Display order on Bob's dash. `bucket` is the Weekly Inventory bucket the tile opens.
export const OEM_BRANDS = [
  { key: 'ditchwitch', label: 'Ditch Witch', bucket: 'rigs',       what: 'rigs' },
  { key: 'vermeer',    label: 'Vermeer',     bucket: 'rigs',       what: 'rigs' },
  { key: 'kobelco',    label: 'Kobelco',     bucket: 'excavators', what: 'excavators' },
  { key: 'linkbelt',   label: 'Link-Belt',   bucket: 'excavators', what: 'excavators' },   // #OEM_LINKBELT_V1
];
export const oemLabel = (key) => (OEM_BRANDS.find(b => b.key === key) || {}).label || 'Factory feed';

// A reading that came off a feed (Geotab or a factory feed), as opposed to one a person typed.
export const isApiSrc = (source) => source === 'geotab' || source === 'oem';

// VINs never contain I, O or Q, so a serial typed with one is a typo for 1 / 0. Folding them on BOTH
// sides lets "IVRY240GJR1002029" on the Master still find Vermeer's "1VRY240GJR1002029".
export const vinLoose = (v) => vinClean(v).replace(/I/g, '1').replace(/[OQ]/g, '0');

// serial → machine, across every id the feed gave us for it
export const oemIndex = (blob) => {
  const m = {};
  ((blob && blob.devices) || []).forEach(d => {
    if (!d) return;
    [d.vin, ...(Array.isArray(d.ids) ? d.ids : [])].forEach(id => { const k = vinLoose(id); if (k && k.length >= 8 && !m[k]) m[k] = d; });
  });
  return m;
};
export const oemForUnit = (index, vin) => { const k = vinLoose(vin); return (k && index && index[k]) || null; };

// The reading taken for the week that starts on `mondayISO` — never "whatever the feed says today".
export const oemWeekHours = (device, mondayISO) => {
  const w = device && device.weekly && device.weekly[mondayISO];
  const h = w ? Number(w.h) : NaN;
  return isFinite(h) ? h : null;
};
export const oemWeekAt = (device, mondayISO) => {
  const w = device && device.weekly && device.weekly[mondayISO];
  return (w && w.at) || null;
};

// One brand's state for the dash tile: 'live' | 'waiting' | 'error'
export const oemBrandStatus = (blob, key) => {
  const p = (blob && blob.providers && blob.providers[key]) || null;
  const devs = ((blob && blob.devices) || []).filter(d => d && d.provider === key);
  const withHours = devs.filter(d => d.engineHours != null && !d.stale).length;
  const silent = devs.filter(d => d.stale || d.engineHours == null).length;
  const state = !p || p.status === 'waiting' ? 'waiting' : (p.status === 'error' ? 'error' : 'live');
  return { state, machines: devs.length, withHours, silent, at: (p && p.at) || null, checkedAt: (p && p.checkedAt) || null, message: (p && p.message) || '' };
};

// ══════════════════════════════════════════════════════════
//  #FLEET_RIG_BOARD_V1 (2026-10-08) — what the rig board on Bob's dash reads off each machine.
//  Bill: "Codes are important or inop issues, idle, inner rod etc. … RIGs are the Backbone of our company."
//  Vermeer sends, on top of the hour meter: idle hours, the last rod report off the rack, its own service
//  schedule, open fault codes, factory bulletins and warranty dates (api/oem-hours-sync.js keeps them as
//  workingHours / rod / pm / faults / faultList / campaigns / warrantyEnd, plus one `daily` reading per day).
//  There is NO inner-rod hour counter in the feed — only inner rotation speed at the moment of a rod report.
//  Every helper tolerates a machine that carries none of it (Ditch Witch / Kobelco until we see their data).
// ══════════════════════════════════════════════════════════
const num = (v) => { const n = Number(v); return v != null && v !== '' && isFinite(n) ? n : null; };

// When the tracker last spoke, and how that reads against the pull: 'active' (within a day and a half),
// 'parked' (heard from, but not lately), 'silent' (the feed's own stale rule — never auto-fills).
export const oemActivity = (d, pulledAt) => {
  if (!d) return { state: 'none', at: null, days: null };
  const times = [d.lastReportAt, d.engineHoursAt].map(t => (t ? new Date(t).getTime() : NaN)).filter(t => isFinite(t));
  const at = times.length ? Math.max(...times) : null;
  if (d.stale || d.deactivated || at == null) return { state: 'silent', at: at == null ? null : new Date(at).toISOString(), days: null };
  const ref = pulledAt ? new Date(pulledAt).getTime() : Date.now();
  const hrs = Math.max(0, (ref - at) / 3600000);
  return { state: hrs <= 36 ? 'active' : 'parked', at: new Date(at).toISOString(), days: Math.floor(hrs / 24) };
};

// Lifetime split of the hour meter: working vs idling. Vermeer's own workingHours = total − idle.
export const oemSplit = (d) => {
  const total = num(d && d.engineHours), idle = num(d && d.idleHours);
  if (total == null || total <= 0 || idle == null) return { total, idle, work: null, idlePct: null };
  const work = Math.max(0, num(d.workingHours) != null ? num(d.workingHours) : total - idle);
  return { total, idle, work, idlePct: Math.min(100, Math.max(0, Math.round((idle / total) * 100))) };
};

// Hours run (and idled) since this week's Monday reading — only once that reading exists.
export const oemSinceMonday = (d, mondayISO) => {
  const w = d && d.weekly && d.weekly[mondayISO];
  const h0 = w ? num(w.h) : null, h1 = num(d && d.engineHours);
  if (h0 == null || h1 == null || h1 < h0) return null;
  const run = Math.round((h1 - h0) * 10) / 10;
  const i0 = num(w.idle), i1 = num(d.idleHours);
  const idle = (i0 != null && i1 != null && i1 >= i0) ? Math.min(run, Math.round((i1 - i0) * 10) / 10) : null;
  return { run, idle };
};

// Day-by-day bars from the daily readings: each bar is one reading to the next (so a Saturday-to-Monday
// bar covers the weekend). A meter that went backwards or jumped further than the clock allows is skipped.
export const oemDailyBars = (d, n = 14) => {
  const days = Object.keys((d && d.daily) || {}).sort();
  const out = [];
  for (let i = 1; i < days.length; i++) {
    const a = d.daily[days[i - 1]], b = d.daily[days[i]];
    const h0 = num(a && a.h), h1 = num(b && b.h);
    if (h0 == null || h1 == null) continue;
    const span = Math.max(1, Math.round((new Date(days[i] + 'T12:00:00Z') - new Date(days[i - 1] + 'T12:00:00Z')) / 86400000));
    const run = Math.round((h1 - h0) * 10) / 10;
    if (run < 0 || run > span * 24 + 1) continue;
    const i0 = num(a.idle), i1 = num(b.idle);
    const idle = (i0 != null && i1 != null && i1 >= i0) ? Math.min(run, Math.round((i1 - i0) * 10) / 10) : null;
    out.push({ from: days[i - 1], to: days[i], span, run, idle, work: idle == null ? null : Math.round((run - idle) * 10) / 10 });
  }
  return out.slice(-n);
};

// YYYY-MM-DD → M/D/YYYY with no time-zone math (a calendar date is not a moment).
export const mdy = (dateISO) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateISO || '')); return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : ''; };

// The maker's own service schedule for this machine, as one line. `mark` is the hour mark of the service
// that is due (Vermeer's "1,250-hour service"). tone: 'bad' | 'warn' | 'ok' | 'none'.
export const oemService = (d) => {
  const pm = d && d.pm;
  if (!pm) return { tone: 'none', text: '', tip: '' };
  const until = num(pm.hoursUntil), mark = num(pm.mark);
  const what = mark != null ? `${mark.toLocaleString('en-US')}-hr service` : 'Service';
  const last = num(pm.lastHours) != null
    ? `Last service logged with the maker: ${num(pm.lastHours).toLocaleString('en-US')} h${pm.lastDate ? ' on ' + mdy(pm.lastDate) : ''}.`
    : 'No service has been logged with the maker for this machine.';
  if (until != null && until < 0) return { tone: 'bad', text: `${what} past due by ${Math.abs(Math.round(until)).toLocaleString('en-US')} h`, tip: last };
  if (until != null && until <= 50) return { tone: 'warn', text: `${what} due in ${Math.round(until)} h`, tip: last };
  if (until != null) return { tone: 'ok', text: `${what} in ${Math.round(until).toLocaleString('en-US')} h`, tip: last };
  if (/past|over/i.test(pm.status || '')) return { tone: 'bad', text: `${what} past due`, tip: last };
  return { tone: 'none', text: pm.status ? `${what}: ${String(pm.status).replace(/_/g, ' ').toLowerCase()}` : '', tip: last };
};

// Open fault codes as readable lines. The feed keeps each code's plain fields whatever the maker calls them,
// so pick the likeliest ones by name rather than trusting one spelling.
export const oemFaultLines = (d) => ((d && d.faultList) || []).map(f => {
  if (!f || typeof f !== 'object') return '';
  const keys = Object.keys(f);
  const pick = (re) => { const k = keys.find(x => re.test(x) && f[x] !== '' && f[x] != null); return k ? String(f[k]) : ''; };
  const spn = pick(/^spn$/i), fmi = pick(/^fmi$/i);
  const code = spn ? `SPN ${spn}${fmi ? ' / FMI ' + fmi : ''}` : pick(/code|dtc/i);
  const desc = pick(/desc|text|message|name/i), sev = pick(/sever|level|lamp/i), cnt = pick(/count|occur/i);
  return [code, desc, sev ? `(${sev})` : '', cnt && cnt !== '0' && cnt !== '1' ? `×${cnt}` : ''].filter(Boolean).join(' ') || f.text || '';
}).filter(Boolean);

// Open fault codes, counted and graded. Vermeer sends one entry per severity ({ severity:'AMBER', count:1 }), so the
// total is the sum of the counts. tone: 'bad' (red / stop) · 'warn' (amber) · 'note' (notification only) · 'ok' (none)
// · 'none' (the maker sends no fault data for this machine).
export const oemFaults = (d) => {
  if (!d || (d.faults == null && !Array.isArray(d.faultList))) return { known: false, total: 0, tone: 'none', parts: [], text: '' };
  const by = {};
  ((d && d.faultList) || []).forEach(f => {
    if (!f || typeof f !== 'object') return;
    const k = Object.keys(f).find(x => /sever|level|lamp/i.test(x));
    const sev = (k ? String(f[k]) : '').trim().toUpperCase() || 'OTHER';
    const c = num(f.count);
    by[sev] = (by[sev] || 0) + (c != null && c >= 0 ? c : 1);
  });
  const listed = Object.keys(by).reduce((n, k) => n + by[k], 0);
  const total = listed || num(d.faults) || 0;
  const has = (re) => Object.keys(by).some(k => re.test(k) && by[k] > 0);
  const tone = !total ? 'ok' : (has(/RED|STOP|CRIT|SEVERE|HIGH/) ? 'bad' : (has(/AMBER|YELLOW|WARN|MED/) ? 'warn' : (listed ? 'note' : 'warn')));
  const order = (k) => (/RED|STOP|CRIT|SEVERE|HIGH/.test(k) ? 0 : (/AMBER|YELLOW|WARN|MED/.test(k) ? 1 : 2));
  const parts = Object.keys(by).filter(k => by[k] > 0).sort((a, b) => order(a) - order(b)).map(k => ({ sev: k, count: by[k], label: `${by[k]} ${k.toLowerCase()}` }));
  return { known: true, total, tone, parts, text: parts.map(x => x.label).join(' · ') };
};

// ── #FLEET_RIG_FAULT_WATCH_V1 (2026-10-08) — Bill: fault codes "as it happens … does not need to wait for Monday."
// The pull runs every 15 minutes and stamps a rig when a code appears or gets worse (faultNewAt), keeps when the
// current codes were first seen (faultSince) and, when the maker's fault history gives it, the code text (faultCodes).
export const oemFaultNew = (d, hours = 24) => {
  const t = d && d.faultNewAt ? new Date(d.faultNewAt).getTime() : NaN;
  return isFinite(t) && (Date.now() - t) < hours * 3600000 && (num(d.faults) || 0) > 0;
};
// "SPN 6588 / FMI 31 — Operator Shutdown With High Exhaust Temperature · logged 10/2" per stored history entry,
// newest first (real layout seen on 26-0120). Entries with neither a code nor a description are left out.
export const oemCodeLines = (d) => ((d && Array.isArray(d.faultCodes)) ? d.faultCodes : []).map(f => {
  if (!f || typeof f !== 'object') return '';
  const keys = Object.keys(f);
  const pick = (re) => { const k = keys.find(x => re.test(x) && f[x] !== '' && f[x] != null && typeof f[x] !== 'boolean'); return k ? String(f[k]) : ''; };
  const spn = pick(/^spn$/i), fmi = pick(/^fmi$/i);
  const code = spn ? `SPN ${spn}${fmi ? ' / FMI ' + fmi : ''}` : pick(/code|dtc/i);
  const desc = pick(/desc|text|message/i);
  if (!code && !desc) return '';
  // when the maker logged it — a calendar day is enough; the feed stores it as `at`
  const t = f.at ? new Date(f.at) : null;
  const day = t && !isNaN(t.getTime()) ? ` · logged ${t.getMonth() + 1}/${t.getDate()}` : '';
  return [code, desc].filter(Boolean).join(' — ') + day;
}).filter(Boolean);

// Where the rig last reported from, as a map link. Vermeer sends a position with every report.
export const oemMapUrl = (d) => {
  const lat = num(d && d.lat), lon = num(d && d.lon);
  if (lat == null || lon == null || (Math.abs(lat) < 0.01 && Math.abs(lon) < 0.01) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return '';
  return `https://www.google.com/maps?q=${lat.toFixed(5)},${lon.toFixed(5)}`;
};
